import { Box, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { tokens } from '../../global/theme/tokens';
import MoneyLabel from '../label/MoneyLabel';
import { useTranslation } from 'react-i18next';

// Compact KPI tile: icon row, value, optional trend arrow with % (tooltip explains "vs prev").
const KpiTile = ({ label, value, type = 'money', icon, trend, isLoading, onClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const renderValue = () => {
    if (isLoading) return <Skeleton width="60%" height={24} />;
    if (type === 'money') {
      return <MoneyLabel money={value ?? 0} variant="h6" />;
    }
    return (
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
        {value ?? 0}
      </Typography>
    );
  };

  const renderTrend = () => {
    if (!trend || trend.pct === null || trend.pct === undefined) return null;
    const pct = trend.pct;
    if (pct === 0) return null;
    const positive = pct > 0;
    const color = positive ? colors.money?.positive : colors.money?.negative;
    const Icon = positive ? ArrowUpwardIcon : ArrowDownwardIcon;
    const formatted = `${positive ? '+' : ''}${(pct * 100).toFixed(0)}%`;
    return (
      <Tooltip title={t('vs prev. period')} placement="top">
        <Box
          className="flex items-center gap-0.5"
          sx={{ color, fontSize: '0.72rem', fontWeight: 500 }}
        >
          <Icon sx={{ fontSize: '0.85rem' }} />
          <span>{formatted}</span>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.selected}`,
        borderRadius: '10px',
        padding: 1.25,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        cursor: onClick ? 'pointer' : 'default',
        minHeight: 76,
        transition: 'border-color 0.15s ease',
        '&:hover': onClick ? { borderColor: colors.text } : {},
      }}
    >
      <Box className="flex items-center justify-between">
        <Box className="flex items-center gap-1">
          {icon && (
            <Box sx={{ color: colors.text, opacity: 0.55, display: 'flex' }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              color: colors.text,
              opacity: 0.65,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 0.4,
            }}
          >
            {label}
          </Typography>
        </Box>
        {renderTrend()}
      </Box>
      {renderValue()}
    </Box>
  );
};

export default KpiTile;
